import { useState } from "react";
import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import PolicyCTA from "@/components/policy/PolicyCTA";
import PolicyHero from "@/components/policy/PolicyHero";
import RevealStyles from "@/components/policy/RevealStyles";
import {
  Bot,
  Shield,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Scale,
  Eye,
  FileText,
  Truck,
  Factory,
  Building2,
  HardHat,
  UtensilsCrossed,
  Headphones,
  Hotel,
  Briefcase,
  Hammer,
  HeartPulse,
  Lock,
  Database,
  Gavel,
  Siren,
  Zap,
  ClipboardCheck,
} from "lucide-react";

const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-white/10 bg-white/[0.03]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
      >
        <span className="font-bold text-white pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white/30 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-white/50 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const targetedJobs = [
  { icon: Truck, label: "Drivers & truckers" },
  { icon: Factory, label: "Warehouse workers" },
  { icon: UtensilsCrossed, label: "Restaurant & food service" },
  { icon: Hammer, label: "Manufacturing workers" },
  { icon: Briefcase, label: "Bank tellers & loan processors" },
  { icon: Headphones, label: "Call center workers" },
  { icon: Hotel, label: "Hotel & casino service" },
  { icon: Building2, label: "Cashiers & retail workers" },
  { icon: Shield, label: "Security guards" },
  { icon: HardHat, label: "Skilled trades & construction" },
  { icon: HeartPulse, label: "Healthcare support staff" },
  { icon: Bot, label: "Delivery workers" },
];

const jobImpactReport = [
  "How many Nevada jobs may be eliminated",
  "Which job categories are affected",
  "Expected wage loss",
  "Expected reduction in hours",
  "Whether full-time workers are being replaced with machines",
  "Whether benefits will be reduced",
  "What human oversight jobs will remain",
  "Whether displaced workers will be retrained",
  "Whether workers will be offered equivalent jobs at equivalent pay",
  "How the automation affects public safety",
  "How much profit the company expects to gain",
];

const transitionFundUses = [
  "Retraining displaced workers",
  "Wage support",
  "Apprenticeship programs",
  "Technical certification",
  "Job placement",
  "Small business support",
  "Public-sector hiring pipelines",
  "Emergency support for workers harmed by automation",
  "Grants for workers starting local businesses",
  "Training for AI oversight, repair, safety inspection, and maintenance jobs",
];

const humanOversightAreas = [
  "Self-driving fleets",
  "Trucking automation",
  "Heavy machinery",
  "Warehouse robotics",
  "Restaurant automation involving heat, blades, pressure, or movement",
  "Manufacturing robotics",
  "Medical or elder-care AI",
  "Hotel & casino service automation",
  "Essential service phone systems",
  "Systems affecting housing, healthcare, utilities, employment, banking, or insurance",
];

const divisionOversight = [
  "AI registration",
  "Certification",
  "Safety testing",
  "Insurance verification",
  "Job impact reports",
  "Incident investigations",
  "Public reporting",
  "Enforcement",
  "Fines",
  "Workplace automation reviews",
];

const fundingSources = [
  "Licensing fees",
  "Testing fees",
  "Certification fees",
  "Annual company assessments",
  "Device registration fees",
  "Incident investigation fees",
  "Enforcement fines",
  "Safety surcharges",
  "Insurance verification fees",
];

const testingCategories = [
  "Human detection testing",
  "Child detection testing",
  "Worker proximity testing",
  "Emergency stop testing",
  "Sensor failure testing",
  "Power failure testing",
  "Misclassification testing",
  "Weather & environmental testing",
  "Nighttime testing",
  "Mechanical failure testing",
  "Cybersecurity testing",
  "Edge-case testing",
  "Bias / discrimination testing",
  "Real-world simulation testing",
  "Black-box log validation",
  "Human override validation",
  "Failure containment testing",
];

const coveredExamples = [
  "Self-driving cars",
  "Autonomous trucks",
  "Delivery robots",
  "Drones",
  "Warehouse robots",
  "Manufacturing robots",
  "Restaurant & burger-flipping robots",
  "AI forklifts",
  "Hotel robots",
  "Casino service automation",
  "AI security systems",
  "AI medical devices",
  "Automated phone & customer service systems",
  "AI banking systems",
  "AI insurance decision systems",
  "AI hiring systems",
  "AI housing screening systems",
  "AI utility & healthcare systems",
];

const liabilityExamples = [
  "If a burger robot mistakes a worker's hand for food and burns them, the company is responsible.",
  "If a self-driving vehicle kills a pedestrian, the company is responsible.",
  "If a warehouse robot crushes a worker, the company is responsible.",
  "If an AI phone system blocks someone from emergency healthcare or essential services, the company is responsible.",
];

const fineTiers = [
  { tier: "Minor injury", value: "Minimum % of annual revenue", color: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
  { tier: "Serious injury", value: "Higher % of annual revenue", color: "bg-orange-500/10 border-orange-500/30 text-orange-200" },
  { tier: "Permanent disability / disfigurement", value: "Higher % of annual revenue", color: "bg-red-500/10 border-red-500/30 text-red-200" },
  { tier: "Death", value: "Minimum 10% of yearly revenue", color: "bg-red-600/15 border-red-600/40 text-red-100" },
];

const escapePrevention = [
  "Parent company liability",
  "Successor liability",
  "Affiliate liability",
  "Contractor liability",
  "Anti-asset-stripping provisions",
  "Mandatory reserve funds",
  "Emergency asset freeze after serious injury or death",
  "Prohibition on moving assets after incidents",
  "Liability for companies that profit from the AI system",
];

const accountableExecs = [
  "CEO",
  "CTO",
  "CFO",
  "Board members",
  "Safety officers",
  "Compliance officers",
  "Product executives",
  "Engineering leads",
  "Anyone who signs deployment approval",
  "Any person exercising operational control",
];

const personalCertifications = [
  "Safety compliance",
  "Insurance compliance",
  "Testing compliance",
  "Job impact reporting",
  "Incident disclosure",
  "Black-box data preservation",
];

const blackBoxLogs = [
  "Sensor data",
  "AI classifications",
  "Decision records",
  "Software version",
  "Command history",
  "Human override attempts",
  "Emergency stop activation",
  "Maintenance history",
  "Remote control activity",
  "Incident timeline",
];

const publicDbFields = [
  "Company name",
  "System type",
  "Date of incident",
  "Location",
  "Type of harm",
  "Injury or death status",
  "Certification status",
  "Insurance status",
  "Enforcement outcome",
  "Fine amount",
];

const suspensionTriggers = [
  "Death",
  "Amputation",
  "Permanent disability",
  "Serious burn",
  "Crush injury",
  "Hospitalization",
  "Serious vehicle crash",
  "Multiple injuries",
  "Repeated near-misses",
  "Black-box tampering",
  "Failure to report incidents",
];

const essentialServices = [
  "Healthcare",
  "Housing",
  "Banking",
  "Insurance",
  "Utilities",
  "Emergency services",
  "Unemployment benefits",
  "Public benefits",
  "Employment screening",
  "Transportation",
];

const protectedWorkers = [
  "Drivers",
  "Warehouse workers",
  "Manufacturing workers",
  "Hotel workers",
  "Casino workers",
  "Restaurant workers",
  "Cashiers",
  "Bank workers",
  "Delivery workers",
  "Call center workers",
  "Customer service workers",
  "Construction workers",
  "Healthcare support workers",
  "Security workers",
  "Pedestrians",
  "Consumers",
  "Children",
  "Seniors",
  "Disabled Nevadans",
  "Small businesses",
];

const fightingFor = [
  "Nevada workers",
  "Safe technology",
  "Fair wages",
  "Family-supporting jobs",
  "Human accountability",
  "Consumer safety",
  "Responsible innovation",
  "Corporations paying the full cost of their risks",
  "No taxpayer subsidies for AI oversight",
  "No corporate immunity when machines hurt people",
];

const faqs = [
  {
    question: "Is Cooper anti-technology?",
    answer:
      "No. Cooper is pro-worker, pro-safety, and pro-accountability. AI can be part of Nevada's future — it just cannot be allowed to wipe out jobs, injure people, and leave taxpayers and families with the bill.",
  },
  {
    question: "Who pays for the new regulatory division?",
    answer:
      "100% the companies deploying AI in Nevada. Zero percent comes from Nevada taxpayers. Licensing, testing, certification, registration, assessments, and enforcement fines fully fund the Nevada Autonomous Systems Safety Division.",
  },
  {
    question: "What is the Job Impact Report?",
    answer:
      "Before a company can deploy AI or automation that may replace Nevada workers, they must file a public report disclosing how many jobs are affected, wage impact, benefit reductions, displaced worker retraining plans, and the profits the company expects to gain.",
  },
  {
    question: "What is strict liability?",
    answer:
      "If a covered AI system harms a Nevadan, the company that developed, deployed, owned, leased, operated, licensed, or profited from it is responsible. Victims should not have to prove intent or that executives ignored warnings.",
  },
  {
    question: "How big are the fines?",
    answer:
      "Revenue-based and scaled to harm. A death triggers a minimum fine of at least 10% of the company's yearly revenue — separate from compensation owed to victims and families. Fines must be large enough that billion-dollar companies cannot write off human injury as a business expense.",
  },
  {
    question: "What stops companies from hiding behind shell companies?",
    answer:
      "Parent company liability, successor liability, affiliate and contractor liability, anti-asset-stripping rules, mandatory reserve funds, and emergency asset freezes after serious injury or death.",
  },
];

const sources = [
  { title: "Nevada Revised Statutes — Labor & Workplace Safety", desc: "Existing NV statutes governing workplace conditions and worker protections" },
  { title: "U.S. Bureau of Labor Statistics — Occupational Outlook", desc: "Federal data on at-risk occupations and projected automation exposure" },
  { title: "National Highway Traffic Safety Administration (NHTSA)", desc: "Federal data on autonomous vehicle incidents and reporting requirements" },
  { title: "OSHA — Robot & Automation Safety Standards", desc: "Federal occupational safety standards for industrial robotics" },
  { title: "MIT Work of the Future Task Force", desc: "Research on the impact of automation on workers and communities" },
  { title: "Brookings Institution — Automation & Workforce", desc: "Studies on automation exposure across U.S. industries and regions" },
  { title: "Nevada Department of Employment, Training and Rehabilitation", desc: "Nevada workforce data and displaced worker programs" },
  { title: "Federal AI Risk Management Frameworks (NIST AI RMF)", desc: "National standards on AI testing, oversight, and accountability" },
];

const AutomationPage = () => {
  useReveal();

  return (
    <div data-testid="automation-page" className="bg-[#0B1A3B]">
      {/* ── HERO ── */}
      <PolicyHero
        testId="automation-hero"
        align="left"
        maxWidth="6xl"
        minHeight="90vh"
        badge={{ icon: Bot, label: "Nevada AI Public Safety & Job Protection Act" }}
        headline={
          <>
            AI Should Work For Nevada Workers —{" "}
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Not Replace Them, Injure Them, And Leave Families With The Bill.
            </span>
          </>
        }
      >
        <div className="bg-white/[0.04] border-l-4 border-[#D4AF37] backdrop-blur-sm p-6 mb-8 max-w-4xl">
          <p className="text-white/80 leading-relaxed text-lg">
            Driving jobs. Warehouse jobs. Manufacturing jobs. Restaurant jobs.
            Banking jobs. Customer service jobs. Delivery jobs. Hotel jobs.
            Casino jobs. Construction jobs. Call center jobs.{" "}
            <span className="text-[#D4AF37] font-bold">
              These are not just numbers on a spreadsheet. These are Nevada families.
            </span>
          </p>
        </div>

        <p className="text-lg sm:text-xl text-white/60 max-w-3xl mb-10 leading-relaxed">
          Cooper's <span className="text-white font-bold">Nevada AI Public Safety and Job Protection Act</span> will
          make sure companies cannot replace workers with untested AI, operate
          dangerous autonomous systems without insurance, or escape
          accountability when their machines harm people.
        </p>

        <div className="flex flex-wrap gap-4">
          <a
            href="#the-problem"
            data-testid="hero-read-plan-btn"
            className="group inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.35)]"
          >
            Read The Plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <Link
            to="/volunteer"
            data-testid="hero-protect-workers-btn"
            className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest text-sm transition-all"
          >
            Protect Nevada Workers
          </Link>
          <a
            href="#accountability"
            data-testid="hero-accountability-btn"
            className="inline-flex items-center gap-2 px-8 py-4 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 font-bold uppercase tracking-widest text-sm transition-all"
          >
            Hold AI Companies Accountable
          </a>
        </div>

        <p className="mt-10 text-xs uppercase tracking-[0.3em] text-[#D4AF37]/70 font-bold">
          "AI should work for Nevada — not replace Nevada."
        </p>
      </PolicyHero>

      {/* ── SECTION 1: THE PROBLEM ── */}
      <section id="the-problem" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 01 — The Problem</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6 max-w-4xl">
              AI Companies Want Nevada Workers Replaced.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed max-w-3xl">
              Major corporations are actively researching and deploying AI and
              automation to replace human workers across every industry that
              keeps Nevada families afloat:
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
            {targetedJobs.map((job) => (
              <div key={job.label} className="bg-white/[0.04] border border-white/10 p-5 hover:border-[#D4AF37]/30 transition-colors">
                <job.icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                <p className="text-white/80 font-bold text-sm">{job.label}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-gradient-to-r from-[#D4AF37]/10 via-[#D4AF37]/5 to-transparent border-l-4 border-[#D4AF37] p-8">
            <p className="text-white/80 text-lg leading-relaxed mb-4">
              These jobs support Nevada families. They pay rent. They buy
              groceries. They support children. They keep local communities
              alive. When corporations eliminate them, the savings go to
              executives and shareholders while workers are left behind.
            </p>
            <p className="text-[#D4AF37] font-bold text-xl leading-snug">
              "Cooper will not let Nevada become a testing ground where
              billion-dollar companies replace workers, lower wages, and call it
              innovation."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: PRINCIPLE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 02 — The Principle</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              No Automation Without Accountability.
            </h2>
          </div>

          <div className="reveal relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/20 to-transparent blur-2xl" />
            <div className="relative bg-[#0F2251] border-2 border-[#D4AF37]/40 p-8 md:p-12">
              <Scale className="w-10 h-10 text-[#D4AF37] mb-6" />
              <p className="text-2xl md:text-3xl font-black text-white leading-tight mb-6">
                "If AI replaces a Nevadan, endangers a Nevadan, or makes
                decisions that affect a Nevadan's life, then that company must
                be regulated, insured, tested, and held accountable."
              </p>
              <p className="text-white/60 leading-relaxed">
                Cooper is not anti-technology. He is anti-recklessness. He is
                anti-corporate immunity. He is anti-using Nevada workers and
                families as crash-test dummies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: JOB IMPACT REPORT ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 03 — Job Protection First</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Before AI Replaces Nevada Workers, Companies Must Answer To Nevada.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Any company deploying AI or automation that may replace workers
              must file a public{" "}
              <span className="text-white font-bold">Nevada Job Impact Report</span>{" "}
              before deployment.
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-3 mb-10">
            {jobImpactReport.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-4">
                <ClipboardCheck className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "If a company wants to replace Nevada workers with AI, the public
              has a right to know who is being replaced, why, and who profits."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 4: TRANSITION FUND ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 04 — Worker Transition Fund</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Nevada Worker Transition & Job Protection Fund
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Companies replacing workers with AI must pay into a worker
              protection fund. Used for:
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
            {transitionFundUses.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-colors">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/[0.08] border-2 border-emerald-500/30 p-6">
              <p className="text-3xl font-black text-emerald-300 mb-2">100%</p>
              <p className="text-white/80 font-bold uppercase tracking-wider text-sm">Paid by AI companies</p>
            </div>
            <div className="bg-red-500/[0.06] border-2 border-red-500/30 p-6">
              <p className="text-3xl font-black text-red-300 mb-2">0%</p>
              <p className="text-white/80 font-bold uppercase tracking-wider text-sm">Paid by Nevada taxpayers</p>
            </div>
          </div>

          <div className="reveal mt-10 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-l-4 border-[#D4AF37] p-6">
            <p className="text-[#D4AF37] font-bold text-xl leading-snug">
              "If AI creates productivity gains, Nevada workers should share in
              those gains — not be discarded by them."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: HUMAN OVERSIGHT ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 05 — Mandatory Oversight</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Humans Stay In The Loop For High-Risk Systems.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Companies cannot remove humans completely from high-risk systems.
              Mandatory human oversight is required for:
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-3 mb-10">
            {humanOversightAreas.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-4">
                <Users className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "No company should be allowed to fire the humans, deploy the
              machines, and then claim nobody was responsible when the machine
              fails."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: NV AUTONOMOUS SYSTEMS SAFETY DIVISION ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 06 — Enforcement Body</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Regulating AI Without Charging Nevada Taxpayers One Penny.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Establish the{" "}
              <span className="text-white font-bold">
                Nevada Autonomous Systems Safety Division
              </span>{" "}
              to oversee:
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-12">
            {divisionOversight.map((item) => (
              <div key={item} className="bg-white/[0.04] border border-white/10 p-4 text-center">
                <p className="text-white/80 font-bold text-sm">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-5">Funding Sources</h3>
              <div className="space-y-2">
                {fundingSources.map((s) => (
                  <div key={s} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 p-3">
                    <DollarSign className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/70 text-sm">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center">
              <div className="w-full bg-gradient-to-br from-[#D4AF37]/15 to-transparent border-2 border-[#D4AF37]/40 p-8">
                <p className="text-4xl md:text-5xl font-black text-[#D4AF37] mb-3 leading-tight">0%</p>
                <p className="text-white/80 font-bold uppercase tracking-wider text-sm mb-4">
                  Nevada Taxpayer Funded
                </p>
                <p className="text-4xl md:text-5xl font-black text-white mb-3 leading-tight">100%</p>
                <p className="text-white/80 font-bold uppercase tracking-wider text-sm">
                  Paid By The Companies Creating The Risk
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 7: STATE TESTING ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 07 — State Testing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Mandatory Nevada Testing Before Deployment.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              No more "we tested it ourselves." All covered AI must pass
              independent, state-approved testing — paid fully by the company.
            </p>
          </div>

          <div className="reveal grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
            {testingCategories.map((t) => (
              <div key={t} className="bg-white/[0.04] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-colors">
                <ClipboardCheck className="w-5 h-5 text-[#D4AF37] mb-2" />
                <p className="text-white/75 text-sm font-medium">{t}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "Nevadans should not have to trust a corporation's internal safety
              memo when a machine can crush a hand, cause a wreck, burn a worker,
              or kill a pedestrian."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: COVERED SYSTEMS ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 08 — Scope</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              What Counts As "Covered AI"?
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Any AI or automated system that can see, sense, scan, classify,
              identify, track, move, speak for a company, make decisions
              affecting people, control machinery, operate near humans, replace
              human workers, or affect access to essential services.
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
            {coveredExamples.map((ex) => (
              <div key={ex} className="bg-white/[0.04] border border-white/10 p-3 text-center">
                <p className="text-white/75 text-sm">{ex}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-[#0F2251] border-2 border-white/15 p-6">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-3">Legal Definition</p>
            <p className="text-white/80 italic leading-relaxed">
              "Autonomous or semi-autonomous AI systems capable of perceiving,
              classifying, deciding, communicating, or acting in a manner that
              may affect human safety, employment, access, property, or bodily
              integrity."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 9: INSURANCE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 09 — Insurance</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              If Your Machine Can Hurt A Nevadan, It Must Be Insured.
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { label: "Low-Risk AI", desc: "Basic coverage", color: "border-emerald-500/30 bg-emerald-500/[0.05]" },
              { label: "Moderate-Risk AI", desc: "Higher coverage", color: "border-amber-500/30 bg-amber-500/[0.05]" },
              { label: "High-Risk Physical AI", desc: "Major coverage", color: "border-orange-500/30 bg-orange-500/[0.05]" },
              { label: "Catastrophic-Risk AI", desc: "Highest coverage + state compensation fund", color: "border-red-500/40 bg-red-500/[0.07]" },
            ].map((tier) => (
              <div key={tier.label} className={`border-2 p-5 ${tier.color}`}>
                <Shield className="w-6 h-6 text-[#D4AF37] mb-3" />
                <p className="text-white font-bold mb-2">{tier.label}</p>
                <p className="text-white/60 text-sm">{tier.desc}</p>
              </div>
            ))}
          </div>

          <div className="reveal mb-8">
            <h3 className="text-lg font-bold text-white mb-4">Covered harms include:</h3>
            <div className="flex flex-wrap gap-2">
              {[
                "Burns",
                "Crush injuries",
                "Amputations",
                "Vehicle crashes",
                "Falls",
                "Electrocution",
                "Medical harm",
                "Denial of essential services",
                "Physical injury",
                "Death",
                "Property damage",
              ].map((h) => (
                <span
                  key={h}
                  className="px-3 py-1.5 bg-white/[0.05] border border-white/10 text-white/70 text-sm"
                >
                  {h}
                </span>
              ))}
            </div>
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "If your machine can injure a Nevadan, you must be insured before
              it operates in Nevada."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 10: STRICT LIABILITY ── */}
      <section id="accountability" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 10 — Strict Liability</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              If Your AI Hurts Someone, You Are Responsible.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              If a company develops, deploys, owns, leases, operates, licenses,
              or profits from a covered AI system, and that system harms a human
              being — the company is responsible. Period.
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-red-500/[0.06] border-2 border-red-500/30 p-6">
              <XCircle className="w-7 h-7 text-red-400 mb-3" />
              <p className="text-red-200 font-bold mb-3">Victims should NOT have to prove:</p>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>• Intent</li>
                <li>• Recklessness</li>
                <li>• Negligence</li>
                <li>• That executives knew it was unsafe</li>
                <li>• That the company ignored warnings</li>
              </ul>
            </div>
            <div className="bg-emerald-500/[0.06] border-2 border-emerald-500/30 p-6">
              <CheckCircle className="w-7 h-7 text-emerald-400 mb-3" />
              <p className="text-emerald-200 font-bold mb-3">Strict liability means:</p>
              <p className="text-white/70 text-sm leading-relaxed">
                The company that profits from putting the machine into the world
                is the company responsible when that machine causes harm — full
                stop.
              </p>
            </div>
          </div>

          <div className="reveal space-y-3 mb-10">
            {liabilityExamples.map((ex) => (
              <div key={ex} className="bg-white/[0.04] border-l-4 border-[#D4AF37] p-5">
                <p className="text-white/80 leading-relaxed">{ex}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-l-4 border-[#D4AF37] p-6">
            <p className="text-[#D4AF37] font-bold text-xl leading-snug">
              "Companies should not be allowed to deploy autonomous machines into
              public life and escape responsibility by saying they believed the
              machine was safe."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 11: FINES ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 11 — Revenue-Based Fines</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Fines Big Enough That Billion-Dollar Companies Can't Treat Human Harm As A Cost Of Doing Business.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Nevada will impose mandatory fines based on company revenue when
              AI systems harm people. These are separate from compensation owed
              to victims and families.
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-4 mb-10">
            {fineTiers.map((f) => (
              <div key={f.tier} className={`border-2 p-6 ${f.color}`}>
                <p className="text-xs font-bold uppercase tracking-widest mb-2 opacity-80">{f.tier}</p>
                <p className="text-xl md:text-2xl font-black">{f.value}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "A $10,000 fine means nothing to a billion-dollar company. Nevada's
              penalties must be large enough that corporations cannot write off
              human injury as a business expense."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 12: NO ESCAPE ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 12 — Closing The Escape Hatches</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              No Shell Companies. No Asset Stripping. No Escape.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Companies cannot avoid liability by hiding behind subsidiaries or
              shell companies.
            </p>
          </div>

          <div className="reveal grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
            {escapePrevention.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-4">
                <Lock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "No company should be allowed to create a shell company, deploy
              dangerous AI, pull out the profits, and leave injured Nevadans with
              nothing."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 13: EXECUTIVE ACCOUNTABILITY ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 13 — Executive Accountability</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              The People Who Sign Off Must Be Accountable.
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-bold text-white mb-5">Covered individuals:</h3>
              <div className="space-y-2">
                {accountableExecs.map((p) => (
                  <div key={p} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 p-3">
                    <Gavel className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/75 text-sm">{p}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white mb-5">Personal certification required for:</h3>
              <div className="space-y-2">
                {personalCertifications.map((p) => (
                  <div key={p} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 p-3">
                    <ClipboardCheck className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white/75 text-sm">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="reveal mt-10 bg-white/[0.04] border-l-4 border-[#D4AF37] p-6">
            <p className="text-white/85 text-lg italic leading-relaxed">
              "Nobody should be able to sign off on unleashing autonomous machines
              in Nevada and then hide when people are hurt."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 14 + 15: BLACK BOX + PUBLIC DB ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 14 & 15 — Transparency</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Black-Box Logs & A Public AI Incident Database.
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <Database className="w-6 h-6 text-[#D4AF37]" />
                <h3 className="text-lg font-bold text-white">Tamper-Proof Logs</h3>
              </div>
              <div className="space-y-2">
                {blackBoxLogs.map((b) => (
                  <div key={b} className="flex items-center gap-2 bg-white/[0.03] border border-white/10 p-2.5">
                    <span className="text-[#D4AF37] text-xs">●</span>
                    <span className="text-white/75 text-sm">{b}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-white/55 text-sm italic">
                Failure to preserve logs creates a legal presumption against the company.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-5">
                <Eye className="w-6 h-6 text-[#D4AF37]" />
                <h3 className="text-lg font-bold text-white">Public Incident Database</h3>
              </div>
              <div className="space-y-2">
                {publicDbFields.map((b) => (
                  <div key={b} className="flex items-center gap-2 bg-white/[0.03] border border-white/10 p-2.5">
                    <span className="text-[#D4AF37] text-xs">●</span>
                    <span className="text-white/75 text-sm">{b}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-white/55 text-sm italic">
                "No company should be allowed to quietly injure Nevadans and keep operating in secret."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 16: IMMEDIATE SUSPENSION ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 16 — Immediate Suspension</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              When Machines Cause Serious Harm — They Stop.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Nevada may immediately suspend any covered AI system or fleet
              after any of the following:
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {suspensionTriggers.map((s) => (
              <div key={s} className="flex items-center gap-2 bg-red-500/[0.05] border border-red-500/20 p-3">
                <Siren className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-white/80 text-sm font-medium">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 17: ESSENTIAL SERVICES ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 17 — Essential Services</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              No Nevadan Loses Essential Services To An Algorithm Alone.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              AI used in essential services must include human review and human
              backup. No Nevadan may be denied an essential service solely by AI
              without timely human review.
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {essentialServices.map((e) => (
              <div key={e} className="bg-white/[0.04] border border-white/10 p-4 text-center">
                <p className="text-white/80 font-bold text-sm">{e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 18: WHY NEVADA ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12 max-w-4xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 18 — Why This Matters To Nevada</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Tourism. Hotels. Casinos. Warehouses. Trucking. Construction.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Nevada's economy is built on exactly the industries AI companies
              are targeting for automation. This policy protects:
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-10">
            {protectedWorkers.map((w) => (
              <div key={w} className="bg-white/[0.04] border border-white/10 p-3 text-center hover:border-[#D4AF37]/30 transition-colors">
                <p className="text-white/75 text-sm font-medium">{w}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-l-4 border-[#D4AF37] p-6">
            <p className="text-[#D4AF37] font-bold text-xl leading-snug">
              "Nevada's economy should not be rebuilt around replacing workers,
              cutting payroll, and concentrating wealth at the top."
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 19: WHAT COOPER IS FIGHTING FOR ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Section 19 — What Cooper Is Fighting For</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Pro-Worker. Pro-Safety. Pro-Nevada.
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-3 mb-10">
            {fightingFor.map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white/[0.04] border border-white/10 p-4">
                <Zap className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-white/80 font-bold text-sm">{item}</p>
              </div>
            ))}
          </div>

          <div className="reveal bg-[#0F2251] border-2 border-[#D4AF37]/40 p-8 md:p-10">
            <p className="text-xl md:text-2xl font-black text-white leading-snug">
              "AI can be part of Nevada's future, but it cannot be allowed to
              destroy Nevada's workers, endanger Nevada families, or let
              billion-dollar companies walk away from the damage they cause."
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="relative py-20 md:py-28">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Common Questions
            </h2>
          </div>

          <div className="reveal space-y-3">
            {faqs.map((f) => (
              <FAQItem key={f.question} question={f.question} answer={f.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── SOURCES ── */}
      <section id="sources" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="absolute inset-0 bg-[#0B1A3B]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Sources & Citations</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
              Where The Research Comes From
            </h2>
          </div>

          <div className="reveal grid md:grid-cols-2 gap-4">
            {sources.map((s) => (
              <div key={s.title} className="bg-white/[0.04] border border-white/10 p-4">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-bold text-sm mb-1">{s.title}</p>
                    <p className="text-white/40 text-xs leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="reveal mt-6 text-white/40 text-xs leading-relaxed">
            Citations and statistics will be updated as live research and Nevada
            workforce data are finalized. Specific fine percentages, fund
            contribution rates, and tier coverage values will be set in
            consultation with labor, safety, and insurance experts during
            legislative drafting.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <PolicyCTA
        testId="automation-cta"
        heading={
          <>
            Protect Nevada Jobs. Protect Nevada Families.
            <br />
            <span className="bg-gradient-to-r from-[#D4AF37] to-[#F5D76E] bg-clip-text text-transparent">
              Hold AI Companies Accountable.
            </span>
          </>
        }
        subtitle="Stand with Cooper to keep Nevada workers — and Nevada communities — at the center of the AI economy."
        primary={{ label: "Volunteer", to: "/volunteer" }}
        secondary={{ label: "Contact Us", to: "/contact" }}
        next={{ label: "No AI Data Center Subsidies", to: "/policies/no-ai-subsidies" }}
      />

      <RevealStyles />
    </div>
  );
};

export default AutomationPage;
